<template>
  <div :class="$abem({ block: 'o-reminderForm' })">
    <Title :prefix="4" :text="$t('Receive an email reminder')" />
    <div :class="$abem({ element: 'preText' })">
      <p>
        {{ $t("Enter your details below to receive a reminder by email when your next fertile windows start.") }}
      </p>
    </div>
    <form :class="$abem({ element: 'form' })" :action="salesforceEndpoint" method="POST">

      <!-- Organisation ID -->
      <input type="hidden" name="oid" value="00D90000000J4Zc" />

      <!-- Website source  -->
      <input type="hidden" name="00N900000027Tj2" :value="websiteSource" />

      <!-- Enquiry reason -->
      <input type="hidden" name="00N900000027RyR" value="Ovulation calculator" />

      <!-- Cycle length -->
      <input type="hidden" name="00N5m000000PiLe" :value="cycleLength" />

      <!-- First day of last period -->
      <input type="hidden" name="00N5m000000PiLj" :value="salesforceDateFromIso(lastPeriodDate)" />

      <!-- Next Estimated Fertile Window Start Date -->
      <input type="hidden" name="00N5m000000PiLo" :value="salesforceFertilityDate('month1')('start')" />

      <!-- Next Estimated Fertile Window End Date -->
      <input type="hidden" name="00N5m000000PiLt" :value="salesforceFertilityDate('month1')('end')" />

      <!-- Second Estimated Fertile Window Start Date -->
      <input type="hidden" name="00N5m000000PiM3" :value="salesforceFertilityDate('month2')('start')" />

      <!-- Second Estimated Fertile Window End Date -->
      <input type="hidden" name="00N5m000000PiLy" :value="salesforceFertilityDate('month2')('end')" />

      <!-- Return URL -->
      <input type="hidden" name="retURL" :value="retURL" />

      <!-- First name -->
      <div :class="$abem({ element: 'formItem' })">
        <label :class="$abem({ element: 'formItemLabel' })" for="name">{{ $t("First name") }} <span class="primary--text">*</span></label>
        <input name="first_name" v-model="name" :class="$abem({ element: 'formItemTextField' })" type="text" id="name" />
      </div>

      <!-- Email -->
      <div :class="$abem({ element: 'formItem' })">
        <label :class="$abem({ element: 'formItemLabel' })" for="email">{{ $t("Email") }} <span class="primary--text">*</span></label>
        <input name="email" v-model="email" :class="$abem({ element: 'formItemTextField' })" type="email" id="email" />
      </div>

      <!-- Date of birth -->
      <div :class="$abem({ element: 'formItem' })">
        <label :class="$abem({ element: 'formItemLabel' })" for="div">{{ $t("Date of birth") }}</label>
        <input v-model="dob" :class="$abem({ element: 'formItemTextField' })" type="date" id="dob" />
        <input type="hidden" name="00N90000004VQYl" :value="salesforceDateFromIso(dob)" />
      </div>

      <!-- Subscribe to newsletter -->
      <div :class="$abem({ element: 'formItem' })">
        <label :class="$abem({ element: 'formItemLabel' })" for="subscribe">
          <input name="00N90000002keSl" :class="$abem({ element: 'formItemCheckbox' })" type="checkbox" id="subscribe" value="1" />
          {{ $t('Subscribe to newsletter') }}
        </label>
      </div>

      <div :class="$abem({ element: 'formItemText' })">
        <p>{{ $t("We will send you a reminder of your fertile windows in accordance with our Privacy Policy.") }}</p>
      </div>

      <!-- Privacy agreement -->
      <div :class="$abem({ element: 'formItem' })">
        <label :class="$abem({ element: 'formItemLabel' })" for="agree">
          <input v-model="agree" :class="$abem({ element: 'formItemCheckbox' })" type="checkbox" id="agree" />
          {{ $t('I agree to the terms of the') }} <a href="/privacy-policy" target="_blank">{{ $t('Privacy Policy') }}.</a>
        </label>
        <small>
          {{ $t('Virtus Health will use your information to address your enquiry. If you have consented, we will also send you our newsletter. You can opt out at any time. You can read more about how we will handle your personal information, including how to access or correct your information, and how to make a complaint, in our') }} <a href="/privacy-policy" target="_blank">{{ $t('Privacy Policy') }}.</a>
        </small>
      </div>

      <div :class="$abem({ element: 'formItem' })">
        <button @click="submit($event)" :class="$abem({ element: 'submitButton', additional: [{ primary: valid }] })">{{ $t("Send") }}</button>
      </div>
    </form>
  </div>
</template>

<script>
import { format } from 'date-fns'

import Title from './Title'

function validateEmail(email) {
  const re = /\S+@\S+\.\S+/
  return re.test(String(email).toLowerCase());
}

export default {
  components: {
    Title,
  },
  props: {
    cycleLength: {
      type: Number,
      required: true,
    },
    lastPeriodDate: {
      type: String,
      required: true,
    },
    fertileWindowDates: {
      type: Object,
      required: true,
    },
    urlParameters: {
      type: Object,
      required: true,
    }
  },
  mounted () {
    // The retURL is the return URL that Salesforce web to lead will redirect
    // back to. See App.vue where the `completed` param triggers the
    // CompletedMessage component to be displayed instead of the Calculator
    // component.
    this.retURL = window?.location?.href + '&completed=1'
  },
  data () {
    return {
      name: '',
      email: '',
      agree: false,
      dob: '',
      salesforceEndpoint: 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8',
      retURL: '', // Redirect return URL on submission to Salesforce. Updates in mounted().
    }
  },
  computed: {
    valid () {
      return !!this.name && validateEmail(this.email) && !!this.agree
    },
    salesforceFertilityDate () {
      return monthKey => position => {
        if (!this.fertileWindowDates[monthKey]) {
          return ''
        }
        const positionIndex = (position === 'start' ? 0 : this.fertileWindowDates[monthKey].length -1)
        return this.salesforceDateFromIso(this.fertileWindowDates[monthKey][positionIndex])
      }
    },
    websiteSource () {
      if (!this.urlParameters.website_source) {
        return ''
      }
      return decodeURI(this.urlParameters.website_source)
    }
  },
  methods: {
    submit (event) {
      if (!this.valid) {
        event.preventDefault()
      }
      // If valid standard HTML form submission action is taken.
    },
    salesforceDateFromIso (isoDate) {
      const [year, month, day] = isoDate.split('-')
      if (!year || !month || !day) {
        return ''
      }
      return format(new Date(year, month -1, day), 'd/MM/Y', { awareOfUnicodeTokens: true }).split('-')
    }
  }
}
</script>

<style lang="scss">
.o-reminderForm {
  p {
    margin: 0;
  }
  &__preText {
    margin: 25px;
  }
  &__form {
    display: block;
    margin: 0 auto;
    padding: 2rem 10%;
    margin: 25px;
    background-color: #ececed;
  }
  &__formItem {
    display: block;
    margin-bottom: 1.5rem;
    &:last-child {
      margin-bottom: 0;
    }
  }
  &__formItemLabel {
    display: inline-block;
    min-width: 125px;
  }
  &__formItemTextField {
    display: inline-block;
    background-color: #fff;
    font-size: 1rem;
    padding: 0.5rem;
    min-width: 250px;
    &:hover, &:focus {
      box-shadow: 0 0 0 2px #999;
      outline: 0;
    }
  }
  &__formItemCheckbox {
    width: 20px;
    height: 20px;
    display: inline-block;
    margin: 0.5rem 0.5rem 0 0;
    transform: translateY(5px);
  }

  &__submitButton {
    display: inline-block;
    background-color: #ccc;
    min-width: 125px;
    padding: 12px 24px;
    color: #fff;
  }
}
</style>